import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPinIcon, StarIcon, WifiIcon, HomeIcon, CarIcon, AirVentIcon, TvIcon, UtensilsIcon, BedIcon, CalendarIcon, UsersIcon, ChevronLeftIcon, ChevronRightIcon, HeartIcon, ShareIcon } from 'lucide-react';
import { allProperties } from '../utils/mockData';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useBooking } from '../context/BookingContext';
const PropertyDetailsPage = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    setBookingDetails
  } = useBooking();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [guests, setGuests] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showAllImages, setShowAllImages] = useState(false);
  useEffect(() => {
    // Simulate API call
    const fetchProperty = () => {
      setLoading(true);
      const foundProperty = allProperties.find(p => p.id === parseInt(id));
      if (foundProperty) {
        setProperty(foundProperty);
      } else {
        // Property not found
        navigate('/search');
      }
      setLoading(false);
    };
    fetchProperty();
  }, [id, navigate]);
  const handleBooking = () => {
    if (!checkInDate || !checkOutDate) {
      alert('Please select check-in and check-out dates');
      return;
    }
    // Calculate number of nights
    const nights = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
    // Calculate total price
    const totalPrice = property.pricePerNight * nights;
    // Set booking details in context
    setBookingDetails({
      propertyId: property.id,
      propertyTitle: property.title,
      propertyImage: property.images[0],
      checkInDate,
      checkOutDate,
      guests,
      nights,
      pricePerNight: property.pricePerNight,
      totalPrice
    });
    // Navigate to booking page
    navigate(`/booking/${property.id}`);
  };
  const nextImage = () => {
    setCurrentImageIndex(prevIndex => prevIndex === property.images.length - 1 ? 0 : prevIndex + 1);
  };
  const prevImage = () => {
    setCurrentImageIndex(prevIndex => prevIndex === 0 ? property.images.length - 1 : prevIndex - 1);
  };
  if (loading) {
    return <div className="container mx-auto px-4 py-12 flex justify-center" data-id="element-959">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4A017]" data-id="element-960"></div>
      </div>;
  }
  if (!property) {
    return <div className="container mx-auto px-4 py-12" data-id="element-961">
        <div className="bg-white rounded-lg shadow-md p-8 text-center" data-id="element-962">
          <h2 className="text-2xl font-bold mb-4" data-id="element-963">Property Not Found</h2>
          <p className="mb-6" data-id="element-964">
            The property you're looking for doesn't exist or has been removed.
          </p>
          <button onClick={() => navigate('/search')} className="bg-[#006D77] hover:bg-[#00595f] text-white px-6 py-3 rounded-md font-medium" data-id="element-965">
            Back to Search
          </button>
        </div>
      </div>;
  }
  return <div className="bg-[#F8F5F0] min-h-screen w-full" data-id="element-966">
      <div className="container mx-auto px-4 py-6" data-id="element-967">
        {/* Property Title & Location */}
        <div className="mb-6" data-id="element-968">
          <h1 className="text-2xl md:text-3xl font-bold text-[#333333] mb-2" data-id="element-969">
            {property.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4" data-id="element-970">
            <div className="flex items-center" data-id="element-971">
              <StarIcon className="h-5 w-5 text-[#D4A017] fill-[#D4A017]" data-id="element-972" />
              <span className="ml-1 font-medium" data-id="element-973">{property.rating}</span>
              <span className="text-gray-500 ml-1" data-id="element-974">
                ({property.reviewCount} reviews)
              </span>
            </div>
            <div className="flex items-center" data-id="element-975">
              <MapPinIcon className="h-5 w-5 text-gray-500" data-id="element-976" />
              <span className="ml-1 text-gray-700" data-id="element-977">{property.location}</span>
            </div>
          </div>
        </div>
        {/* Property Images */}
        {showAllImages ? <div className="fixed inset-0 bg-black z-50 p-4 overflow-y-auto" data-id="element-978">
            <button className="absolute top-4 right-4 bg-white rounded-full p-2" onClick={() => setShowAllImages(false)} data-id="element-979">
              <XIcon className="h-6 w-6" data-id="element-980" />
            </button>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-12" data-id="element-981">
              {property.images.map((image, index) => <div key={index} className="aspect-w-16 aspect-h-9" data-id="element-982">
                  <img src={image} alt={`${property.title} - image ${index + 1}`} className="w-full h-full object-cover" data-id="element-983" />
                </div>)}
            </div>
          </div> : <div className="relative mb-8" data-id="element-984">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 h-[300px] md:h-[400px]" data-id="element-985">
              <div className="md:col-span-2 md:row-span-2 relative" data-id="element-986">
                <img src={property.images[currentImageIndex]} alt={`${property.title} - main image`} className="w-full h-full object-cover rounded-tl-lg rounded-bl-lg" data-id="element-987" />
                <div className="absolute inset-0 flex items-center justify-between px-4" data-id="element-988">
                  <button className="bg-white rounded-full p-1 shadow-md hover:bg-gray-100" onClick={prevImage} data-id="element-989">
                    <ChevronLeftIcon className="h-5 w-5" data-id="element-990" />
                  </button>
                  <button className="bg-white rounded-full p-1 shadow-md hover:bg-gray-100" onClick={nextImage} data-id="element-991">
                    <ChevronRightIcon className="h-5 w-5" data-id="element-992" />
                  </button>
                </div>
              </div>
              <div className="hidden md:block" data-id="element-993">
                <img src={property.images[1 % property.images.length]} alt={`${property.title} - image 2`} className="w-full h-full object-cover" data-id="element-994" />
              </div>
              <div className="hidden md:block" data-id="element-995">
                <img src={property.images[2 % property.images.length]} alt={`${property.title} - image 3`} className="w-full h-full object-cover rounded-tr-lg" data-id="element-996" />
              </div>
              <div className="hidden md:block" data-id="element-997">
                <img src={property.images[3 % property.images.length]} alt={`${property.title} - image 4`} className="w-full h-full object-cover" data-id="element-998" />
              </div>
              <div className="hidden md:block relative" data-id="element-999">
                <img src={property.images[4 % property.images.length]} alt={`${property.title} - image 5`} className="w-full h-full object-cover rounded-br-lg" data-id="element-1000" />
                <button className="absolute bottom-4 right-4 bg-white bg-opacity-90 px-4 py-2 rounded-md font-medium" onClick={() => setShowAllImages(true)} data-id="element-1001">
                  Show all photos
                </button>
              </div>
            </div>
            <div className="absolute top-4 right-4 flex space-x-2" data-id="element-1002">
              <button className="bg-white rounded-full p-2 shadow-md hover:bg-gray-100" data-id="element-1003">
                <ShareIcon className="h-5 w-5" data-id="element-1004" />
              </button>
              <button className="bg-white rounded-full p-2 shadow-md hover:bg-gray-100" data-id="element-1005">
                <HeartIcon className="h-5 w-5" data-id="element-1006" />
              </button>
            </div>
          </div>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1007">
          {/* Property Details */}
          <div className="md:col-span-2" data-id="element-1008">
            {/* Host Info */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-id="element-1009">
              <div className="flex justify-between items-start" data-id="element-1010">
                <div data-id="element-1011">
                  <h2 className="text-xl font-semibold mb-2" data-id="element-1012">
                    {property.propertyType === 'bnb' ? 'Bed & Breakfast' : 'Farmstay'}{' '}
                    hosted by {property.host.name}
                  </h2>
                  <p className="text-gray-600" data-id="element-1013">
                    {property.maxGuests} guests · {property.bedrooms} bedrooms ·{' '}
                    {property.beds} beds · {property.bathrooms} bathrooms
                  </p>
                </div>
                <img src={property.host.avatar} alt={property.host.name} className="w-12 h-12 rounded-full" data-id="element-1014" />
              </div>
            </div>
            {/* Description */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-id="element-1015">
              <h2 className="text-xl font-semibold mb-4" data-id="element-1016">About this place</h2>
              <p className="text-gray-700 mb-4" data-id="element-1017">{property.description}</p>
            </div>
            {/* Amenities */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-id="element-1018">
              <h2 className="text-xl font-semibold mb-4" data-id="element-1019">
                What this place offers
              </h2>
              <div className="grid grid-cols-2 gap-4" data-id="element-1020">
                {property.amenities.includes('Wi-Fi') && <div className="flex items-center" data-id="element-1021">
                    <WifiIcon className="h-5 w-5 mr-2 text-gray-600" data-id="element-1022" />
                    <span data-id="element-1023">Wi-Fi</span>
                  </div>}
                {property.amenities.includes('Kitchen') && <div className="flex items-center" data-id="element-1024">
                    <UtensilsIcon className="h-5 w-5 mr-2 text-gray-600" data-id="element-1025" />
                    <span data-id="element-1026">Kitchen</span>
                  </div>}
                {property.amenities.includes('Free parking') && <div className="flex items-center" data-id="element-1027">
                    <CarIcon className="h-5 w-5 mr-2 text-gray-600" data-id="element-1028" />
                    <span data-id="element-1029">Free parking</span>
                  </div>}
                {property.amenities.includes('Air conditioning') && <div className="flex items-center" data-id="element-1030">
                    <AirVentIcon className="h-5 w-5 mr-2 text-gray-600" data-id="element-1031" />
                    <span data-id="element-1032">Air conditioning</span>
                  </div>}
                {property.amenities.includes('TV') && <div className="flex items-center" data-id="element-1033">
                    <TvIcon className="h-5 w-5 mr-2 text-gray-600" data-id="element-1034" />
                    <span data-id="element-1035">TV</span>
                  </div>}
                {property.amenities.includes('Private bathroom') && <div className="flex items-center" data-id="element-1036">
                    <div className="h-5 w-5 mr-2 text-gray-600" data-id="element-1037" />
                    <span data-id="element-1038">Private bathroom</span>
                  </div>}
                {property.amenities.includes('Bedroom comforts') && <div className="flex items-center" data-id="element-1039">
                    <BedIcon className="h-5 w-5 mr-2 text-gray-600" data-id="element-1040" />
                    <span data-id="element-1041">Bedroom comforts</span>
                  </div>}
                {property.amenities.includes('Entire home') && <div className="flex items-center" data-id="element-1042">
                    <HomeIcon className="h-5 w-5 mr-2 text-gray-600" data-id="element-1043" />
                    <span data-id="element-1044">Entire home</span>
                  </div>}
              </div>
            </div>
            {/* Reviews */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-id="element-1045">
              <div className="flex items-center mb-4" data-id="element-1046">
                <StarIcon className="h-5 w-5 text-[#D4A017] fill-[#D4A017]" data-id="element-1047" />
                <span className="ml-1 font-semibold text-lg" data-id="element-1048">
                  {property.rating}
                </span>
                <span className="text-gray-500 ml-1" data-id="element-1049">
                  ({property.reviewCount} reviews)
                </span>
              </div>
              <div className="space-y-6" data-id="element-1050">
                {property.reviews.slice(0, 3).map((review, index) => <div key={index} className="border-b border-gray-200 pb-4 last:border-0 last:pb-0" data-id="element-1051">
                    <div className="flex items-center mb-2" data-id="element-1052">
                      <img src={review.user.avatar} alt={review.user.name} className="w-10 h-10 rounded-full mr-3" data-id="element-1053" />
                      <div data-id="element-1054">
                        <h4 className="font-medium" data-id="element-1055">{review.user.name}</h4>
                        <p className="text-gray-500 text-sm" data-id="element-1056">{review.date}</p>
                      </div>
                    </div>
                    <p className="text-gray-700" data-id="element-1057">{review.comment}</p>
                  </div>)}
              </div>
              {property.reviews.length > 3 && <button className="mt-4 text-[#006D77] font-medium hover:underline" data-id="element-1058">
                  Show all {property.reviewCount} reviews
                </button>}
            </div>
            {/* Location */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-id="element-1059">
              <h2 className="text-xl font-semibold mb-4" data-id="element-1060">Location</h2>
              <p className="text-gray-700 mb-4" data-id="element-1061">{property.location}</p>
              <div className="h-64 bg-gray-200 rounded-lg" data-id="element-1062">
                {/* Map would go here - using a placeholder */}
                <div className="h-full w-full flex items-center justify-center bg-gray-200 text-gray-500" data-id="element-1063">
                  <MapPinIcon className="h-8 w-8 mr-2" data-id="element-1064" />
                  <span data-id="element-1065">Map view of {property.location}</span>
                </div>
              </div>
            </div>
          </div>
          {/* Booking Card */}
          <div className="md:col-span-1" data-id="element-1066">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-24" data-id="element-1067">
              <div className="flex justify-between items-start mb-4" data-id="element-1068">
                <div data-id="element-1069">
                  <span className="text-xl font-bold" data-id="element-1070">
                    ${property.pricePerNight}
                  </span>
                  <span className="text-gray-500" data-id="element-1071"> / night</span>
                </div>
                <div className="flex items-center" data-id="element-1072">
                  <StarIcon className="h-4 w-4 text-[#D4A017] fill-[#D4A017]" data-id="element-1073" />
                  <span className="ml-1" data-id="element-1074">{property.rating}</span>
                  <span className="text-gray-500 ml-1" data-id="element-1075">
                    ({property.reviewCount})
                  </span>
                </div>
              </div>
              <div className="border border-gray-300 rounded-md mb-4" data-id="element-1076">
                <div className="grid grid-cols-2 divide-x divide-gray-300" data-id="element-1077">
                  <div className="p-3" data-id="element-1078">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1079">
                      CHECK-IN
                    </label>
                    <div className="relative" data-id="element-1080">
                      <CalendarIcon className="absolute left-0 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-1081" />
                      <DatePicker selected={checkInDate} onChange={date => setCheckInDate(date)} selectsStart startDate={checkInDate} endDate={checkOutDate} minDate={new Date()} placeholderText="Add date" className="pl-6 w-full focus:outline-none" data-id="element-1082" />
                    </div>
                  </div>
                  <div className="p-3" data-id="element-1083">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1084">
                      CHECKOUT
                    </label>
                    <div className="relative" data-id="element-1085">
                      <CalendarIcon className="absolute left-0 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-1086" />
                      <DatePicker selected={checkOutDate} onChange={date => setCheckOutDate(date)} selectsEnd startDate={checkInDate} endDate={checkOutDate} minDate={checkInDate || new Date()} placeholderText="Add date" className="pl-6 w-full focus:outline-none" data-id="element-1087" />
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-300 p-3" data-id="element-1088">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1089">
                    GUESTS
                  </label>
                  <div className="relative" data-id="element-1090">
                    <UsersIcon className="absolute left-0 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-1091" />
                    <select value={guests} onChange={e => setGuests(parseInt(e.target.value))} className="pl-6 w-full focus:outline-none appearance-none" data-id="element-1092">
                      {[...Array(property.maxGuests)].map((_, i) => <option key={i + 1} value={i + 1} data-id="element-1093">
                          {i + 1} {i === 0 ? 'guest' : 'guests'}
                        </option>)}
                    </select>
                  </div>
                </div>
              </div>
              <button onClick={handleBooking} className="w-full bg-[#FF6B6B] hover:bg-[#ff5252] text-white py-3 rounded-md font-medium mb-4" data-id="element-1094">
                Reserve
              </button>
              <p className="text-center text-sm text-gray-500 mb-4" data-id="element-1095">
                You won't be charged yet
              </p>
              {checkInDate && checkOutDate && <div className="space-y-3" data-id="element-1096">
                  <div className="flex justify-between" data-id="element-1097">
                    <span className="text-gray-700 underline" data-id="element-1098">
                      ${property.pricePerNight} x{' '}
                      {Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24))}{' '}
                      nights
                    </span>
                    <span className="text-gray-700" data-id="element-1099">
                      $
                      {property.pricePerNight * Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24))}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1100">
                    <span className="text-gray-700 underline" data-id="element-1101">
                      Cleaning fee
                    </span>
                    <span className="text-gray-700" data-id="element-1102">$60</span>
                  </div>
                  <div className="flex justify-between" data-id="element-1103">
                    <span className="text-gray-700 underline" data-id="element-1104">Service fee</span>
                    <span className="text-gray-700" data-id="element-1105">$45</span>
                  </div>
                  <div className="border-t border-gray-300 pt-3 mt-3 flex justify-between font-semibold" data-id="element-1106">
                    <span data-id="element-1107">Total before taxes</span>
                    <span data-id="element-1108">
                      $
                      {property.pricePerNight * Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24)) + 60 + 45}
                    </span>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default PropertyDetailsPage;